import React from 'react'
import { Link } from 'react-router-dom'
import {
    MapPinIcon,
    ClockIcon,
    CurrencyDollarIcon,
    UserGroupIcon,
    HeartIcon as HeartIconOutline,
    EyeIcon,
    CheckBadgeIcon,
    CheckCircleIcon
} from '@heroicons/react/24/outline'
import { PencilSquareIcon } from '@heroicons/react/24/outline'
import { FiClock } from 'react-icons/fi'
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid'
import { Gig } from '@/types/gig'
import { Application } from '@/types/application'
import { User } from '@/types/auth'
import { formatTimeAgo } from '@/utils/formatters'
import { getCategoryIcon } from '@/utils/categoryUtils'
import Chip from '@/components/ui/Chip'
import CountdownTimer from '@/components/ui/CountdownTimer'
import Avatar from '@/components/ui/Avatar'
import GigImageHero from '@/components/ui/GigImageHero'
import { CurrencyDisplay } from '@/components/currency'


interface GigCardProps {
    gig: Gig
    onFavorite?: (gigId: string) => void
    isFavorited?: boolean
    showEmployer?: boolean
    currentUser?: User | null
    onEdit?: (gigId: string) => void
}

const GigCard: React.FC<GigCardProps> = ({
    gig,
    onFavorite,
    isFavorited = false,
    showEmployer = true,
    currentUser = null,
    onEdit
}) => {
    const employer = typeof gig.employer === 'object' ? (gig.employer as User) : null

    const isOwner = Boolean(currentUser && employer && ((currentUser as any)._id || (currentUser as any).id) === ((employer as any)._id || (employer as any).id))

    // Helper to get category name/slug whether it's string or object
    const getCategoryString = () => {
        if (typeof gig.category === 'string') {
            return gig.category
        }
        return gig.category?.name || gig.category?.slug || 'unknown'
    }

    const categoryString = getCategoryString()
    const categoryIcon = getCategoryIcon(categoryString)

    // Has the logged-in worker already applied to this gig? Get the application
    const userApplication = React.useMemo(() => {
        if (currentUser?.role !== 'worker' || !Array.isArray(gig.applications)) {
            return null
        }

        return gig.applications.find((app) => {
            const workerId = typeof app.worker === 'object' ? (app.worker as any)._id : app.worker
            const userId = (currentUser as any)._id || (currentUser as any).id
            return workerId?.toString() === userId?.toString()
        }) as Application | undefined
    }, [currentUser, gig.applications])

    const hasApplied = Boolean(userApplication)

    // Derived totals to handle different API field names and ensure numbers are shown
    const applicationsTotal = (gig as any).applicantsCount ?? (gig as any).applicationCount ?? (Array.isArray(gig.applications) ? gig.applications.length : 0)
    const viewsTotal = (gig as any).viewsCount ?? (gig as any).viewCount ?? 0

    const handleFavoriteClick = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (onFavorite) {
            onFavorite(gig._id)
        }
    }

    const handleEditClick = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (onEdit) {
            onEdit(gig._id)
            return
        }

        // fallback: navigate to edit page
        window.location.href = `/dashboard/gigs/${gig._id}/edit`
    }



    return (
        <div className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-xl dark:border-gray-700 dark:bg-gray-800">
            <Link to={`/gigs/${gig._id}`} className="block">
                {/* Hero Image Section */}
                {gig.images && gig.images.length > 0 && (
                    <div className="relative">
                        <GigImageHero
                            images={gig.images}
                            alt={gig.title}
                            className="h-40 sm:h-44 md:h-48"
                            aspectRatio="banner"
                            overlay={true}
                            autoSlide={gig.images && gig.images.length > 1}
                            slideInterval={4000}
                        >
                            {/* Content overlay on hero image */}
                            <div className="absolute inset-0 flex flex-col justify-between p-2.5 sm:p-3">
                                {/* Action buttons - positioned at top right */}
                                <div className="flex items-start justify-end">
                                    <div className="flex items-center gap-1 sm:gap-2">
                                        {onFavorite && (
                                            <button
                                                onClick={handleFavoriteClick}
                                                className="flex-shrink-0 rounded-full bg-black/50 p-1.5 transition-colors hover:bg-black/70 sm:p-2"
                                                title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                                            >
                                                {isFavorited ? (
                                                    <HeartIconSolid className="w-3 h-3 sm:w-4 sm:h-4 text-red-500" />
                                                ) : (
                                                    <HeartIconOutline className="w-3 h-3 sm:w-4 sm:h-4 text-white hover:text-red-500" />
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Category and status chips - positioned at bottom left */}
                                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                                    <div className="flex items-center gap-1 bg-black/50 px-2 py-1 backdrop-blur-sm sm:gap-2 sm:px-3 sm:py-1.5 rounded-lg">
                                        <span className="text-base sm:text-lg text-white">
                                            {categoryIcon}
                                        </span>
                                        <span className="text-white text-xs sm:text-sm font-medium whitespace-nowrap">
                                            {gig.urgency ? gig.urgency.toUpperCase() : 'MEDIUM'}
                                        </span>
                                    </div>

                                    {gig.featured && (
                                        <Chip
                                            variant="warning"
                                            size="sm"
                                            icon={<span>⭐</span>}
                                            className="shadow-lg text-xs sm:text-sm"
                                        >
                                            Featured
                                        </Chip>
                                    )}

                                    {hasApplied && userApplication && (
                                        <Chip
                                            variant={userApplication.status === 'accepted' ? 'success' :
                                                userApplication.status === 'rejected' ? 'error' :
                                                    userApplication.status === 'completed' ? 'info' : 'success'}
                                            size="sm"
                                            icon={<CheckCircleIcon />}
                                            className="shadow-lg text-xs sm:text-sm"
                                        >
                                            Applied - {userApplication.status.charAt(0).toUpperCase() + userApplication.status.slice(1)}
                                        </Chip>
                                    )}
                                </div>
                            </div>
                        </GigImageHero>
                    </div>
                )}

                {/* Card Content */}
                <div className="p-4 sm:p-5">
                    {/* Countdown Timer for Accepted Applications - Only for gigs requiring submission */}
                    {hasApplied && userApplication?.status === 'accepted' && gig.duration && gig.requireSubmission !== false && (
                        <div className="mb-3 rounded-xl border border-orange-200 bg-orange-50 p-2.5 dark:border-orange-800 dark:bg-orange-900/20 sm:mb-4 sm:p-3">
                            <div className="flex items-center gap-2 text-orange-700 dark:text-orange-300 mb-1.5">
                                <FiClock className="w-4 h-4 flex-shrink-0" />
                                <span className="text-xs sm:text-sm font-semibold">Time remaining:</span>
                            </div>
                            <div className="ml-6">
                                <CountdownTimer
                                    startTime={userApplication.updatedAt}
                                    duration={gig.duration.estimated}
                                    endDate={gig.duration.endDate}
                                    className="text-xs sm:text-sm text-orange-800 dark:text-orange-200 font-medium"
                                    compact={true}
                                />
                            </div>
                        </div>
                    )}

                    {/* Title */}
                    <h3 className="heading-card mb-2 line-clamp-2 break-words text-base leading-tight text-gray-900 transition-colors group-hover:text-primary-600 dark:text-white dark:group-hover:text-primary-400 sm:mb-3 sm:text-lg xl:text-xl">
                        {gig.title}
                    </h3>

                    {/* Description */}
                    <p className="mb-3 line-clamp-2 text-xs leading-normal text-gray-600 dark:text-gray-400 sm:mb-4 sm:text-sm">
                        {gig.description}
                    </p>

                    {/* Payment and location info - stack on mobile */}
                    <div className="mb-3 grid gap-2 sm:mb-4 sm:gap-3">
                        <div className="flex min-w-0 items-center gap-2 rounded-lg bg-emerald-50 px-2.5 py-2 text-sm font-semibold text-green-700 dark:bg-emerald-900/20 dark:text-green-300 sm:text-base">
                            <CurrencyDollarIcon className="w-4 h-4 flex-shrink-0" />
                            <div className="truncate">
                                <CurrencyDisplay
                                    amount={gig.payment.amount}
                                    currency={gig.payment.currency}
                                    compact={true}
                                    showOriginal={true}
                                    showTooltip={true}
                                    variant="success"
                                    className="text-green-600 dark:text-green-400"
                                />
                            </div>
                            {gig.payment.type === 'hourly' && (
                                <span className="text-xs sm:text-sm flex-shrink-0">/hr</span>
                            )}
                        </div>

                        <div className="flex min-w-0 items-center gap-2 rounded-lg bg-slate-100 px-2.5 py-2 text-xs text-gray-600 dark:bg-gray-700/40 dark:text-gray-300 sm:text-sm">
                            <MapPinIcon className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{gig.location.location}</span>
                        </div>
                    </div>

                    {/* Duration and applications - responsive flex layout */}
                    <div className="mb-3 flex flex-col gap-2 text-xs sm:mb-4 sm:text-sm">
                        <div className="flex min-w-0 items-center gap-2 text-gray-600 dark:text-gray-300">
                            <ClockIcon className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{gig.duration.estimated}</span>
                        </div>

                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                <UserGroupIcon className="w-4 h-4 flex-shrink-0" />
                                <span>{applicationsTotal}</span>
                                <span className="hidden sm:inline text-gray-500 dark:text-gray-400">applications</span>
                                <span className="sm:hidden text-gray-500 dark:text-gray-400">apps</span>
                            </div>

                            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                <EyeIcon className="w-4 h-4 flex-shrink-0" />
                                <span>{viewsTotal}</span>
                                <span className="hidden sm:inline text-gray-500 dark:text-gray-400">views</span>
                            </div>
                        </div>
                    </div>

                    {/* Tags - responsive wrapping */}
                    {gig.tags && gig.tags.length > 0 && (
                        <div className="mb-3 flex flex-wrap gap-1.5 sm:mb-4 sm:gap-2">
                            {gig.tags.slice(0, 2).map((tag, index) => (
                                <span
                                    key={index}
                                    className="inline-block truncate rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300 sm:px-2.5 sm:py-1.5 sm:text-sm"
                                >
                                    #{tag}
                                </span>
                            ))}
                            {gig.tags.length > 2 && (
                                <span className="inline-block whitespace-nowrap rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-400 sm:px-2.5 sm:py-1.5 sm:text-sm">
                                    +{gig.tags.length - 2}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Employer info and timestamp - stack on mobile */}
                    <div className="flex flex-col gap-2 border-t border-gray-100 pt-3 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:pt-4">
                        {showEmployer && employer && (
                            <div className="flex items-start gap-2 min-w-0 flex-1">
                                <div className="relative flex-shrink-0">
                                    <Avatar
                                        avatarId={employer.profile.avatar}
                                        alt={`${employer.profile.firstName} ${employer.profile.lastName}`}
                                        size="sm"
                                        className="bg-primary-100 dark:bg-primary-900"
                                        fallbackIcon={
                                            <span className="text-xs sm:text-sm font-medium text-primary-600 dark:text-primary-300">
                                                {employer.profile.firstName[0]}{employer.profile.lastName[0]}
                                            </span>
                                        }
                                    />
                                    {employer.profile.verified && (
                                        <CheckBadgeIcon className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 text-green-500" />
                                    )}
                                </div>

                                <div className="min-w-0 flex-1">
                                    <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white truncate">
                                        {employer.profile.company || `${employer.profile.firstName} ${employer.profile.lastName}`}
                                    </p>
                                    {employer.ratings && employer.ratings.count > 0 && (
                                        <div className="flex items-center gap-1 text-xs flex-wrap">
                                            <span className="text-yellow-400 flex-shrink-0">⭐</span>
                                            <span className="text-gray-600 dark:text-gray-400 truncate">
                                                {employer.ratings.average.toFixed(1)} ({employer.ratings.count})
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap flex-shrink-0 sm:text-right">
                            {formatTimeAgo(gig.createdAt)}
                        </div>
                    </div>
                </div>
            </Link>
        </div>
    )
}

export default GigCard